import { collection, orderBy, query } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { stageLabel } from '@/lib/format';
import type { Candidate, CandidateStage } from '@/types/domain';
import { strings } from '@/strings';

const COLUMNS: CandidateStage[] = ['contacted', 'interviewed', 'vetted', 'matched', 'active'];

/**
 * A board rather than a table: the question a recruiter asks this screen is
 * "who is stuck and on what", and a stage column answers it at a glance where
 * a sortable column does not.
 *
 * Consent is surfaced here because it is the most common reason someone sits
 * in `interviewed` forever — vetting refuses without it.
 */
export function CandidatesPage() {
  const { rows, loading, error } = useDocs<Candidate>(
    query(collection(db, 'candidates'), orderBy('createdAt', 'desc')),
    []
  );

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold text-primary">{strings.candidatesTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && rows.length === 0 ? <p className="text-muted">{strings.candidatesEmpty}</p> : null}

      <div className="grid gap-4 md:grid-cols-5">
        {COLUMNS.map((stage) => {
          const inStage = rows.filter((row) => row.stage === stage);
          if (rows.length > 0 && inStage.length === 0) return null;
          return (
            <section key={stage} className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-muted">{stageLabel(stage)}</h2>
              {inStage.map((row) => (
                <Link
                  key={row.id}
                  to={`/recruiter/candidates/${row.id}`}
                  className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-white p-4 hover:border-primary"
                >
                  <span className="font-medium text-ink">{row.name}</span>
                  {row.currentRole ? <span className="text-xs text-muted">{row.currentRole}</span> : null}
                  {!row.consent?.granted ? (
                    <span className="text-xs text-warning">{strings.consentPending}</span>
                  ) : null}
                </Link>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
